import { useState, useEffect } from 'react';
import {
  bool,
  func,
  object,
  oneOf,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import {
  KeyboardDatePicker,
  KeyboardDateTimePicker,
} from '@material-ui/pickers';

const parseLocalDate = (dateStr, settings = {}) => {
  if (!dateStr) return null;
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const parts = dateStr.split('-').map((p) => parseInt(p, 10));
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }
  if (
    typeof dateStr === 'string' &&
    settings?.utcToLocal &&
    settings?.hasTime &&
    /^\d{4}-\d{2}-\d{2}T/.test(dateStr) &&
    !(/[zZ]$|[+-]\d{2}:\d{2}$/.test(dateStr))
  ) {
    return new Date(`${dateStr}Z`);
  }
  return new Date(dateStr);
};

function DateControl(props) {
  const {
    disabled,
    events = {},
    error,
    helperText,
    label,
    name,
    settings = {},
    size,
    onChange,
    variant,
    value,
    trimestre,
    minDate,
    maxDate,
    setDefaultDate,
  } = props;

  const hasValue = value !== undefined && value !== null && value !== '';
  // initialize selected using local-date parser to avoid UTC shift for YYYY-MM-DD strings
  const getSelectedDate = () => (hasValue
    ? parseLocalDate(value, settings)
    : (setDefaultDate ? new Date() : null));
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(getSelectedDate);

  useEffect(() => {
    setSelected(getSelectedDate());
  }, [
    value,
    settings?.hasTime,
    settings?.minPeriod,
    settings?.onlyDate,
    settings?.utcToLocal,
    setDefaultDate,
  ]);

  let format = {
    year: 'YYYY',
    month: 'YYYY MMMM',
  }[settings?.minPeriod] || 'yyyy-MM-DD';

  if (settings?.hasTime) {
    format += ' HH:mm';
  }

  const openTo = {
    year: 'year',
    month: 'month',
  }[settings?.minPeriod] || 'date';

  const views = {
    year: ['year'],
    month: ['year', 'month'],
  }[settings?.minPeriod];

  const handleChange = (date) => {
    try {
      if (!date) {
        const target = {
          name,
          value: '',
        };
        onChange({
          target,
        });

        if (events.onChange) {
          events.onChange(target);
        }

        setSelected(null);
        return;
      }

      let value = new Date(date);

      if (settings?.minPeriod === 'year') {
        value = new Date(value.getFullYear(), 0, 1);
      } else if (settings?.minPeriod === 'month') {
        value = new Date(value.getFullYear(), value.getMonth(), 1);
      } else if (!settings?.hasTime) {
        value = new Date(value.getFullYear(), value.getMonth(), value.getDate());
      }

      if (settings?.onlyDate) {
        value = value.toISOString().split('T').shift();
      }

      const target = {
        name,
        value,
      };
      onChange({
        target,
      });

      if (events.onChange) {
        events.onChange(target);
      }

      setSelected(value);
    } catch (err) {
      setSelected(date);
    }
  };

  const Component = settings?.hasTime
    ? KeyboardDateTimePicker
    : KeyboardDatePicker;

  return (
    <Component
      ampm={false}
      autoOk={true}
      disabled={disabled}
      error={error}
      format={format}
      fullWidth={true}
      helperText={helperText}
      inputVariant={variant}
      emptyLabel={settings?.emptyLabel}
      InputLabelProps={{
        shrink: `${selected}` !== 'Invalid Date',
      }}
      inputProps={{
        placeholder: settings?.placeholder,
      }}
      KeyboardButtonProps={{
        'aria-label': 'change date',
      }}
      label={label}
      name={name}
      onChange={handleChange}
      onClick={() => !disabled && setOpen(true)}
      onClose={() => setOpen(false)}
      open={open}
      openTo={openTo}
      size={size}
      value={selected}
      variant="inline"
      views={views}
      clearable={settings?.clearable}
      shouldDisableDate={(date) => {
        if (!trimestre && !minDate && !maxDate) return false; // Si no hay trimestre, fecha mínima o máxima, no se deshabilita ninguna fecha

        if (trimestre) {
          const varTrimestre = trimestre;
          const month = new Date(date).getMonth();

          const trimestres = {
            1: [0, 1, 2],   // Enero - Marzo
            2: [3, 4, 5],   // Abril - Junio
            3: [6, 7, 8],   // Julio - Septiembre
            4: [9, 10, 11],  // Octubre - Diciembre
          };

          return !trimestres[varTrimestre].includes(month);
        }

        const dt = new Date(date);
        const dateOnly = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());

        if (minDate) {
          const minParsed = parseLocalDate(minDate) || new Date(minDate);
          const minOnly = new Date(minParsed.getFullYear(), minParsed.getMonth(), minParsed.getDate());
          if (dateOnly < minOnly) return true;
        }
        if (maxDate) {
          const maxParsed = parseLocalDate(maxDate) || new Date(maxDate);
          const maxOnly = new Date(maxParsed.getFullYear(), maxParsed.getMonth(), maxParsed.getDate());
          if (dateOnly > maxOnly) return true;
        }
        return false;
      }}
    />
  );
}

DateControl.defaultProps = {
  disabled: false,
  error: false,
  onChange: () => { },
  onBlur: () => { },
  trimestre: null,
  minDate: null,
  maxDate: null,
  setDefaultDate: true,
};

DateControl.propTypes = {
  disabled: bool,
  error: bool,
  events: object,
  helperText: string,
  label: string,
  name: string,
  settings: shape({
    hasTime: bool,
    utcToLocal: bool,
    minPeriod: oneOf([
      'year',
      'month',
      'day',
    ]),
    onlyDate: bool,
  }),
  size: oneOf([
    'small',
    'medium',
  ]),
  onChange: func,
  onBlur: func,
  value: string,
  variant: oneOf([
    'outlined',
    'standard',
  ]),
  trimestre: oneOf([
    1,
    2,
    3,
    4,
  ]),
  minDate: oneOfType([string, object]),
  maxDate: oneOfType([string, object]),
  setDefaultDate: bool,
};

export default DateControl;
